import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Lottie from 'lottie-react';
import { Typography, Fade, Box } from '@mui/material';

import { useAppDispatch, useAppSelector } from '../../hooks';
import { TUserSignUp, userSignUp } from '../../store/AuthSlice/AuthApi';
import { getErrorState } from '../../store/ErrorSlice';
import RegistrationFormCheckingAnimation from '../../lottieAnimations/registration-form.json';
import SignupForm from './SignupForm';
import { getUrlConvertedString } from '../../helper';

const deviceTimeZone =
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Singapore';

const loadingMessages = [
  'Setting up your learning space…',
  'Preparing your dashboard…',
  'Almost there—unlocking your learning journey…',
  'Loading your personalized study plan…',
  'Getting things ready for you…',
  'One moment—setting up your account…',
];

const QuickSignUp: React.FC = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Store
  const errorState = useAppSelector(getErrorState);

  // State
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [signupErrorMessage, setSignUpErrorMessage] = useState('');
  const [hasSignupError, setHasSignupError] = useState(false);

  const { message: errorMessage } = errorState;

  const searchParams = new URLSearchParams(location.search);
  const name = searchParams.get('name') || '';
  const email = searchParams.get('email') || '';
  const phoneNumber = searchParams.get('phoneNumber') || '';
  const curriculum = searchParams.get('curriculum') || '';
  const subject = searchParams.get('subject') || '';

  useEffect(() => {
    initPage();
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (currentMessageIndex < loadingMessages.length - 1) {
      timer = setTimeout(() => {
        setCurrentMessageIndex((prevIndex) => prevIndex + 1);
      }, 3000);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [currentMessageIndex]);

  useEffect(() => {
    if (errorMessage) {
      setSignUpErrorMessage(errorMessage);
      setHasSignupError(true);
    }
  }, [errorMessage]);

  const initPage = async () => {
    let redirectUrl = `/portal/tutors?`;

    if (curriculum) {
      redirectUrl += `curriculum=${getUrlConvertedString(curriculum)}`;

      if (subject) {
        redirectUrl += `&subject=${getUrlConvertedString(subject)}`;
      }
    }

    const currentUrl = new URL(window.location.href);
    const _searchParams = currentUrl.searchParams;

    _searchParams.set('redirect', redirectUrl);

    window.history.replaceState({}, '', currentUrl.href);

    if (name && email && phoneNumber) {
      const apiData: TUserSignUp = {
        name,
        phoneNumber,
        email: email.toLowerCase().trim(),
        timezone: deviceTimeZone,
        clientId: process.env.REACT_APP_TUTOPIYA_CLIENT_ID || '',
        clientSecret: process.env.REACT_APP_TUTOPIYA_CLIENT_SECRET || '',
        source: 'QUICK_SIGN_UP',
      };

      if (curriculum) {
        apiData.curriculum = curriculum;
      }

      try {
        await dispatch(userSignUp(apiData)).unwrap();
      } catch (error) {
        setHasSignupError(true);
        console.error('Signup error:', error);
      }
    }
  };

  const handleFormSubmit = async (data: {
    name: string;
    phone: string;
    email: string;
    countryCode: string;
  }) => {
    setHasSignupError(false);
    setSignUpErrorMessage('');

    let redirectUrl = `/portal/tutors?`;

    if (curriculum) {
      redirectUrl += `curriculum=${getUrlConvertedString(curriculum)}`;

      if (subject) {
        redirectUrl += `&subject=${getUrlConvertedString(subject)}`;
      }
    }

    const apiData: TUserSignUp = {
      name: data.name,
      phoneNumber: data.phone,
      email: data.email.toLowerCase().trim(),
      timezone: deviceTimeZone,
      clientId: process.env.REACT_APP_TUTOPIYA_CLIENT_ID || '',
      clientSecret: process.env.REACT_APP_TUTOPIYA_CLIENT_SECRET || '',
      source: 'QUICK_SIGN_UP',
    };

    if (curriculum) {
      apiData.curriculum = curriculum;
    }

    try {
      await dispatch(userSignUp(apiData)).unwrap();
    } catch (error) {
      setHasSignupError(true);
      console.error('Signup error:', error);
    }
  };

  if (hasSignupError) {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight: 'calc(100vh - 64px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          padding: 2,
        }}
      >
        <SignupForm
          onSubmit={handleFormSubmit}
          errorMessage={signupErrorMessage}
          curriculum={curriculum}
          subject={subject}
          resetErrorMessage={() => setSignUpErrorMessage('')}
          initialData={{
            name,
            phone: phoneNumber,
            email,
            countryCode: '',
          }}
        />
      </Box>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        minHeight: 'calc(100vh - 64px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
      }}
    >
      <Lottie
        animationData={RegistrationFormCheckingAnimation}
        loop
        style={{ maxWidth: '100%', height: '400px', marginTop: '-100px' }}
      />
      <div style={{ paddingTop: '30px' }}>
        <Fade in key={currentMessageIndex} timeout={800}>
          <Typography
            variant="h6"
            sx={{ textAlign: 'center', color: 'text.secondary' }}
          >
            {loadingMessages[currentMessageIndex]}
          </Typography>
        </Fade>
      </div>
    </div>
  );
};

export default QuickSignUp;
